import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { NZ_I18N } from 'ng-zorro-antd/i18n';
import { en_US } from 'ng-zorro-antd/i18n';
import { registerLocaleData } from '@angular/common';
import id from '@angular/common/locales/id';
import {HTTP_INTERCEPTORS, HttpClient, HttpClientModule} from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { icons } from './shared/modules';
import { AuthInterceptor, PreloadInterceptor } from './interceptors';
import { CriticalNetworkErrorInterceptor } from './interceptors/critical-network-error.interceptor';
import { NzModule } from './shared/modules';
import { SharedModule } from './shared/components/shared/shared.module';
import { HttpHeadersInterceptor } from './interceptors/http-headers.interceptor';
import { ProxyModule } from './pages/proxy/proxy.module';
import * as Sentry from '@sentry/angular';
import { Router } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {BdcWalkModule} from "bdc-walkthrough";
import {MatDialogContent} from "@angular/material/dialog";
import {MatButton} from "@angular/material/button";
import {AngularSvgIconModule} from "angular-svg-icon";
import {AngularSvgIconPreloaderModule} from "angular-svg-icon-preloader";
import {popperVariation, provideTippyConfig} from "@ngneat/helipopper";
import {TippyProps} from "@ngneat/helipopper/lib/tippy.types";
import {MapLoaderComponent} from "./pages/dp-map/components/features/map-loader/map-loader.component";
import {TranslateLoader, TranslateModule} from "@ngx-translate/core";
import {TranslateHttpLoader} from "@ngx-translate/http-loader";
import {XDropdownWrapperComponent} from "./shared/x-kit/components/x-dropdown-wrapper/x-dropdown-wrapper.component";
import {NgCircleProgressModule} from "ng-circle-progress";
import {provideComponentStore} from "@ngrx/component-store";
import {HeatmapValuesService} from "./shared/services/heatmap-values.service";
import {WorkspaceStore} from "./pages/workspace/data-access/workspace-companies.store";
import {WorkspaceBoardsStore} from "./pages/workspace/data-access/workspace-boards.store";
import { CookieConsentBannerComponent } from './components/cookie-consent-banner/cookie-consent-banner.component';

registerLocaleData(id);

export const tooltipVariation: Partial<TippyProps> = {
  theme: 'perare',
  arrow: false,
  animation: 'scale',
  trigger: 'mouseenter',
  maxWidth: 350,
  offset: [0, 5],
};

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    NzIconModule.forRoot(icons),
    HttpClientModule,
    NzModule,
    SharedModule,
    ProxyModule,
    BdcWalkModule,
    MatDialogContent,
    MatButton,
    AngularSvgIconModule.forRoot(),
    AngularSvgIconPreloaderModule.forRoot({
      configUrl: './assets/icons-preload/icons.json',
    }),
    MapLoaderComponent,
    TranslateModule.forRoot({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    XDropdownWrapperComponent,
    NgCircleProgressModule.forRoot({
      maxPercent: 100,
      outerStrokeWidth: 4,
      innerStrokeWidth: 0,
      animation: true,
      outerStrokeColor: "#18A0FB",
      innerStrokeColor: "#FFFFFF",
      animationDuration: 300,
      showTitle: false,
      showSubtitle: false,
      showBackground: false,
      showImage: false,
      showUnits: false,
    }),
    CookieConsentBannerComponent,
  ],
  providers: [
    { provide: NZ_I18N, useValue: en_US },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PreloadInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpHeadersInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CriticalNetworkErrorInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    provideAnimationsAsync(),
    provideTippyConfig({
      defaultVariation: 'tooltip',
      variations: {
        tooltip: tooltipVariation,
        popper: popperVariation,
      },
    }),
    provideComponentStore(HeatmapValuesService),
    provideComponentStore(WorkspaceStore),
    provideComponentStore(WorkspaceBoardsStore),
    // Specify ng-circle-progress as an import
  ],
  bootstrap: [AppComponent],
  exports: [],
})
export class AppModule {}

// required for AOT compilation
export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http);
}
